import { keepPreviousData, useQueries, useQuery, type UseQueryOptions } from "@tanstack/react-query";
import type { Entry, Leaderboard, Overall, SpaceEvent } from "@repo/shared-types";
import { BOARD_PAGE, namedBoard } from "@/lib/boards";
import { queries, type BoardQuery } from "@/lib/queries";
import { read } from "@/lib/query";
import {
  ALL_DIVISIONS,
  OVERALL,
  defaultBoard,
  defaultOverallBoard,
  firstBoardWithResults,
  hasOverall,
  isRanked,
  resultsOn,
  type Board,
} from "@/lib/results";
import type { BoardData } from "@/components/leaderboard";

/**
 * An event's boards as the web app reads them. Each part's leaderboard, and
 * the overall ranking, is a screen of its own (decided with Tiago 10
 * October): `boardPath` addresses it, and its page shows the board the
 * address names — or the viewer's own, or the first with results. The
 * event's page reads one line of each, enough to say where the viewer stands
 * and how many results there are: the api sends the viewer's own line
 * whatever page is asked for.
 *
 * Each board is a query of its own (0075). The loaders warm them
 * (`readPartBoard`, `readOverallBoard`) and the pages read them again
 * (`usePartBoard`…), both choosing the board by the same functions.
 */

/** A part's board screen, or the overall's for OVERALL. */
export function boardPath(eventPath: string, partId: string): string {
  return partId === OVERALL ? `${eventPath}/overall` : `${eventPath}/parts/${partId}`;
}

/** Where a board is first read: the one the address names, else the viewer's own. */
type Start = {
  choice: Board;
  /** The address named a division: that board stays, empty or not. */
  named: boolean;
  cursor: string | null;
  limit: number;
};

function partStart(
  part: SpaceEvent["parts"][number],
  entry: Entry | null,
  profileDivision: string | null,
  search: URLSearchParams,
  limit: number,
): Start {
  const named = namedBoard(search, part.id);
  const fallback = defaultBoard(part, entry, profileDivision);
  const choice = {
    partId: part.id,
    level: part.usesLevels ? (named.level ?? fallback.level) : null,
    division: named.division ?? fallback.division,
  };
  return { choice, named: named.division !== null, cursor: named.cursor, limit };
}

function overallStart(
  event: SpaceEvent,
  entry: Entry | null,
  profileDivision: string | null,
  search: URLSearchParams,
  limit: number,
): Start {
  // An overall keeps men and women apart (0089): an address naming All
  // here names nothing.
  const asked = namedBoard(search, OVERALL);
  const named = asked.division === ALL_DIVISIONS.value ? { ...asked, division: null } : asked;
  const fallback = defaultOverallBoard(event, entry, profileDivision);
  const choice = {
    partId: OVERALL,
    level: fallback.level === null ? null : (named.level ?? fallback.level),
    division: named.division ?? fallback.division,
  };
  return { choice, named: named.division !== null, cursor: named.cursor, limit };
}

function partQuery(slug: string, eventId: string, board: Board, cursor: string | null, limit: number) {
  const { level, division } = board;
  return queries.leaderboard(slug, eventId, board.partId, { level, division, cursor, limit });
}

function overallQuery(slug: string, eventId: string, board: Board, cursor: string | null, limit: number) {
  const { level, division } = board;
  return queries.overall(slug, eventId, { level, division, cursor, limit });
}

/** Where to look instead: the first board with results, when the address named none and this one has none. */
function partElsewhere(start: Start, board: Leaderboard): Board | null {
  if (start.named || board.rows.length > 0 || board.teams.length > 0) return null;
  return firstBoardWithResults(board, start.choice.partId);
}

function overallElsewhere(start: Start, board: Overall): Board | null {
  if (start.named || board.rows.length > 0) return null;
  return firstBoardWithResults(board, OVERALL);
}

/** Warms one part's board, as the address names it, else the viewer's own, else the first with results. */
export async function readPartBoard(
  slug: string,
  eventId: string,
  part: SpaceEvent["parts"][number],
  entry: Entry | null,
  profileDivision: string | null,
  search: URLSearchParams,
  limit = BOARD_PAGE,
): Promise<void> {
  const start = partStart(part, entry, profileDivision, search, limit);
  const other = partElsewhere(start, await read(partQuery(slug, eventId, start.choice, start.cursor, limit)));
  if (other) await read(partQuery(slug, eventId, other, null, limit));
}

/** The overall ranking's board, chosen the same way. */
export async function readOverallBoard(
  slug: string,
  event: SpaceEvent,
  entry: Entry | null,
  profileDivision: string | null,
  search: URLSearchParams,
  limit = BOARD_PAGE,
): Promise<void> {
  const start = overallStart(event, entry, profileDivision, search, limit);
  const other = overallElsewhere(start, await read(overallQuery(slug, event.id, start.choice, start.cursor, limit)));
  if (other) await read(overallQuery(slug, event.id, other, null, limit));
}

/** A board query's key: its path's segments and the board. */
type BoardKey = Array<string | BoardQuery>;

/** What a board is read through, a part's or the overall's. */
type BoardKind<B extends Leaderboard | Overall> = {
  kind: BoardData["kind"];
  query: (board: Board, cursor: string | null, limit: number) => UseQueryOptions<B, Error, B, BoardKey>;
  elsewhere: (start: Start, board: B) => Board | null;
};

function partKind(slug: string, eventId: string): BoardKind<Leaderboard> {
  return {
    kind: "part",
    query: (board, cursor, limit) => partQuery(slug, eventId, board, cursor, limit),
    elsewhere: partElsewhere,
  };
}

function overallKind(slug: string, eventId: string): BoardKind<Overall> {
  return {
    kind: "overall",
    query: (board, cursor, limit) => overallQuery(slug, eventId, board, cursor, limit),
    elsewhere: overallElsewhere,
  };
}

/**
 * One board, for its screen, from the reads the loader warmed: the first
 * board, and the one it sends them to when it has no results. Which board is
 * first can move under the page — a fresh read of the viewer's result or
 * division — to one nobody has read yet: the board shown until then stays,
 * named by its own answer, rather than the page waiting blank.
 */
function useBoard<B extends Leaderboard | Overall>({ kind, query, elsewhere }: BoardKind<B>, start: Start) {
  const first = useQuery({ ...query(start.choice, start.cursor, start.limit), placeholderData: keepPreviousData });
  const other = first.data && !first.isPlaceholderData ? elsewhere(start, first.data) : null;
  const second = useQuery(
    other
      ? { ...query(other, null, start.limit), placeholderData: keepPreviousData }
      : { ...query(start.choice, start.cursor, start.limit), enabled: false },
  );
  return shown(kind, start, first, other, second);
}

/**
 * The first board of each of several, for the event's page: only its
 * viewer's own line is read. A board the page now points to and nobody has
 * read yet is left out until it arrives — `useQueries` keeps no previous
 * answer — rather than the page waiting blank.
 */
function useBoards<B extends Leaderboard | Overall>(
  { kind, query, elsewhere }: BoardKind<B>,
  starts: Start[],
): Array<BoardData | null> {
  const firsts = useQueries({ queries: starts.map((s) => query(s.choice, s.cursor, s.limit)) });
  const others = starts.map((s, i) => {
    const data = firsts[i]?.data;
    return data ? elsewhere(s, data) : null;
  });
  const seconds = useQueries({
    queries: starts.map((s, i) => {
      const other = others[i];
      return other ? query(other, null, s.limit) : { ...query(s.choice, s.cursor, s.limit), enabled: false };
    }),
  });
  return starts.map((s, i) => shown(kind, s, firsts[i]!, others[i] ?? null, seconds[i]!));
}

type Read<B> = { data: B | undefined; isPlaceholderData: boolean };

/** What a board shows: the one it was sent to, when it has arrived, else the first. */
function shown<B extends Leaderboard | Overall>(
  kind: BoardData["kind"],
  start: Start,
  first: Read<B>,
  other: Board | null,
  second: Read<B>,
): BoardData | null {
  if (!first.data) return null;
  if (other && second.data) {
    const choice = second.isPlaceholderData ? boardOf(second.data) : other;
    return { kind, choice, board: second.data, cursor: null } as BoardData;
  }
  // A board kept from before says which it is itself.
  const choice = first.isPlaceholderData ? boardOf(first.data) : start.choice;
  return { kind, choice, board: first.data, cursor: first.isPlaceholderData ? null : start.cursor } as BoardData;
}

/** The board an answer is: its part, level and division. */
function boardOf(board: Leaderboard | Overall): Board {
  return { partId: "partId" in board ? board.partId : OVERALL, level: board.level, division: board.division };
}

/** One part's board, for its screen. */
export function usePartBoard(
  slug: string,
  eventId: string,
  part: SpaceEvent["parts"][number],
  entry: Entry | null,
  profileDivision: string | null,
  search: URLSearchParams,
): BoardData | null {
  return useBoard(partKind(slug, eventId), partStart(part, entry, profileDivision, search, BOARD_PAGE));
}

/** The overall ranking's board, for its screen. */
export function useOverallBoard(
  slug: string,
  event: SpaceEvent,
  entry: Entry | null,
  profileDivision: string | null,
  search: URLSearchParams,
): BoardData | null {
  return useBoard(overallKind(slug, event.id), overallStart(event, entry, profileDivision, search, BOARD_PAGE));
}

/**
 * Where the viewer stands on each ranked part's board and on the overall: one
 * line of each, their own, which the api sends whatever page is asked for.
 * The same reads as `readStandings` warms.
 */
export function useStandings(
  slug: string,
  event: SpaceEvent,
  entry: Entry | null,
  profileDivision: string | null,
): { standings: Record<string, Standing>; overall: Standing | null } {
  const theirs = new URLSearchParams();
  const ranked = event.parts.filter(isRanked);
  const parts = useBoards(
    partKind(slug, event.id),
    ranked.map((part) => partStart(part, entry, profileDivision, theirs, 1)),
  );
  const [overall] = useBoards(
    overallKind(slug, event.id),
    hasOverall(event) ? [overallStart(event, entry, profileDivision, theirs, 1)] : [],
  );
  return {
    standings: Object.fromEntries(
      ranked.flatMap((part, index) => {
        const board = parts[index];
        return board ? [[part.id, standingOf(board)]] : [];
      }),
    ),
    overall: overall ? standingOf(overall) : null,
  };
}

/** Warms what `useStandings` reads. */
export async function readStandings(
  slug: string,
  event: SpaceEvent,
  entry: Entry | null,
  profileDivision: string | null,
): Promise<void> {
  const theirs = new URLSearchParams();
  await Promise.all([
    ...event.parts
      .filter(isRanked)
      .map((part) => readPartBoard(slug, event.id, part, entry, profileDivision, theirs, 1)),
    hasOverall(event) ? readOverallBoard(slug, event, entry, profileDivision, theirs, 1) : null,
  ]);
}

/**
 * Where the viewer stands on a board, for the event's page: how many results
 * it has in all — what its link counts — and the viewer's place on their own
 * board with how many are on it, or null while they have no line there
 * (none, or theirs waiting or hidden: off the board, so without a rank).
 */
export type Standing = {
  choice: Board;
  results: number;
  place: { rank: number; of: number } | null;
  /** On the overall: the viewer's points, or their total, as the line shows it. */
  score: string | null;
};

export function standingOf(data: BoardData): Standing {
  const { board, choice } = data;
  const results = board.boards.reduce((sum, count) => sum + count.results, 0);
  const of = resultsOn(board, choice.level, choice.division);
  if (data.kind === "overall") {
    const mine = data.board.mine;
    return {
      choice,
      results,
      place: mine && mine.rank > 0 ? { rank: mine.rank, of } : null,
      score: mine ? (mine.points !== null ? `${mine.points} points` : (mine.total?.display ?? null)) : null,
    };
  }
  // A part done in teams ranks the viewer's team (one a part, since 0036).
  const rank = data.board.mine?.rank ?? data.board.myTeams[0]?.rank ?? 0;
  return { choice, results, place: rank > 0 ? { rank, of } : null, score: null };
}
